import { useState } from 'react';
import { Chip, Confirm, Field, TextInput } from '@affdice/portal-kit';

import type { PlatformApi } from '../../client.ts';
import { t } from '../../messages.ts';
import { lifecycleLabel } from '../../screens.ts';
import { useAbilities } from '../abilities.tsx';
import { operatorActions, type LifecycleMode } from '../entity-actions.ts';
import { LifecycleDialog } from '../LifecycleDialog.tsx';
import { ActionBar } from '../shared.tsx';
import { LoginAsPickerButton } from '../login-as/LoginAs.tsx';
import { operatorOffersLoginAs } from '../login-as/login-as-model.ts';

/**
 * The actions panel of one operator: suspend or reactivate, delete, restore,
 * purge. Which of them show follows `operatorActions` — the operator's
 * lifecycle state and what the viewer holds — and nothing else.
 */
export function OperatorActions({
  api,
  operatorId,
  name,
  state,
  refresh,
  onChanged,
  onDeleted,
}: {
  api: PlatformApi;
  operatorId: string;
  name: string;
  /** The operator's lifecycle state, as the detail read has it. */
  state: string;
  /** Re-read the operator after an act that keeps it open. */
  refresh: () => void;
  /** Told whenever something the operators list shows has changed. */
  onChanged?: (() => void) | undefined;
  /** Told once the operator is deleted or purged, so whoever opened this can close it. */
  onDeleted: () => void;
}) {
  const { ready, can } = useAbilities();
  const [status, setStatus] = useState<'SUSPEND' | 'REACTIVATE' | null>(null);
  const [reason, setReason] = useState('');
  const [lifecycle, setLifecycle] = useState<LifecycleMode | null>(null);
  const actions = operatorActions(state, can);
  // Owner 2026-10-07: "Log in as" was only reachable two clicks into the
  // Team section. Here it opens the picker with this operator chosen.
  const loginAs = operatorOffersLoginAs(state, can);

  if (!ready) return null;

  const LABEL: Record<LifecycleMode, string> = {
    delete: t('ent.life.action.delete'),
    restore: t('ent.life.action.restore'),
    purge: t('ent.life.action.purge'),
    'purge-now': t('ent.life.action.purgeNow'),
  };

  return (
    <>
      <ActionBar count={actions.length + (loginAs ? 1 : 0)} purged={state === 'PURGED'} can={can}>
        <Chip tone={state === 'LIVE' ? 'locked' : state === 'PURGED' ? 'overdue' : 'neutral'}>
          {lifecycleLabel(state)}
        </Chip>
        {loginAs ? <LoginAsPickerButton api={api} operator={{ id: operatorId, name }} /> : null}
        {actions.map((a) =>
          a === 'suspend' || a === 'reactivate' ? (
            <button
              key={a}
              type="button"
              className={a === 'suspend' ? 'danger-quiet' : 'copy'}
              onClick={() => { setReason(''); setStatus(a === 'suspend' ? 'SUSPEND' : 'REACTIVATE'); }}
            >
              {a === 'suspend' ? t('ops.profile.suspend') : t('ops.profile.reactivate')}
            </button>
          ) : (
            <button
              key={a}
              type="button"
              className={a === 'restore' ? 'copy' : 'danger-quiet'}
              onClick={() => setLifecycle(a)}
            >
              {LABEL[a]}
            </button>
          ),
        )}
      </ActionBar>

      {status ? (
        <Confirm
          title={status === 'SUSPEND' ? t('ops.profile.suspendTitle') : t('ops.profile.reactivateTitle')}
          tone={status === 'SUSPEND' ? 'error' : 'note'}
          confirmLabel={status === 'SUSPEND' ? t('ops.profile.suspendConfirm') : t('ops.profile.reactivateConfirm')}
          body={
            <div className="stack">
              <p>{status === 'SUSPEND' ? t('ops.profile.suspendBody') : t('ops.profile.reactivateBody')}</p>
              <Field label={t('common.reason')}>
                <TextInput value={reason} onChange={setReason} />
              </Field>
            </div>
          }
          onCancel={() => setStatus(null)}
          onConfirm={async () => {
            await api.setOperatorStatus(operatorId, status, reason);
            setStatus(null);
            refresh();
            onChanged?.();
          }}
        />
      ) : null}

      {lifecycle ? (
        <LifecycleDialog
          mode={lifecycle}
          kind="operator"
          name={name}
          needsName
          run={(body) => api.operatorDeletion(operatorId, lifecycle === 'purge-now' ? 'purge' : lifecycle, body)}
          onClose={() => setLifecycle(null)}
          onDone={() => {
            const act = lifecycle;
            setLifecycle(null);
            if (act === 'restore') {
              refresh();
              onChanged?.();
            } else {
              onDeleted();
            }
          }}
        />
      ) : null}
    </>
  );
}
